import {
  faChevronDown,
  faChevronRight,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AddCircle, Close } from '@wfp/icons-react';
import { Button, Text } from '@wfp/react';
import { isEmpty } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { generatePath, NavLink } from 'react-router-dom';
import type { Dispatch } from 'redux';

import { DisplayToggleWrapper, SHOW } from 'src/components/DisplayWrapper';
import IssuesDownloadDocButton from 'src/components/IssuesOverviewTemplate/IssuesDownloadDocButton';
import { SUCCESS_REQUEST, UPDATE } from 'src/constants';
import { CrfVersion, resolveCrf } from 'src/crf';
import { requestAPI } from 'src/redux/api/actions';
import { cleanSignalsAction } from 'src/redux/componentSignals/actions';
import {
  COMMENTS_LIST,
  COMMENTS_TARGET_ID,
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import type { IComment, ICommentPermissions, RoutesConfig } from 'src/types';
import CommentForm from './CommentForm';
import CommentList from './CommentList';
import CommentListFilterSet from './CommentListFilterSet';

interface CommentPanelProps {
  officePk?: string | number;
  reportPk?: string | number;
  sectionPk?: string | number;
  id?: string;
  data?: IComment[];
  signals?: Record<string, any>;
  permissions?: ICommentPermissions;
  anchors?: any;
  reportIsPublished?: boolean;
  draftComment?: any;
  routesConfig?: RoutesConfig;
  period?: string | number;
  project_code?: string;
  crf?: CrfVersion;
  user?: string;
  cleanSignals: (signalsToDrop: any) => void;
  fetchCommentsRelatedToSection: (params?: any) => void;
}

interface CommentPanelState {
  collapseForm: boolean;
  selectedFilter: string;
  expandAll: boolean;
}

export class CommentPanelComponent extends Component<
  CommentPanelProps,
  CommentPanelState
> {
  state: CommentPanelState = {
    collapseForm: false,
    selectedFilter: 'all',
    expandAll: true,
  };

  componentDidMount() {
    this.updateCommentsList();
  }

  componentWillUnmount() {
    this.props.cleanSignals({ [COMMENTS_TARGET_ID]: [SHOW] });
  }

  getSectionAPIKwargs = () => {
    return {
      _office_: this.props.officePk,
      _report_: this.props.reportPk,
      _section_: this.props.sectionPk,
    };
  };

  updateCommentsList = () => {
    const sectionAPIKwargs = this.getSectionAPIKwargs();

    if (
      !sectionAPIKwargs?._office_ ||
      !sectionAPIKwargs?._report_ ||
      !sectionAPIKwargs?._section_
    ) {
      return;
    }

    this.props.fetchCommentsRelatedToSection({
      url_format: sectionAPIKwargs,
      storage_key: COMMENTS_LIST,
    });
  };

  componentDidUpdate(prevProps: CommentPanelProps) {
    if (this.props.signals?.[UPDATE] !== prevProps.signals?.[UPDATE]) {
      this.updateCommentsList();
      this.props.cleanSignals({ [this.props.id]: [UPDATE, SUCCESS_REQUEST] });
    }

    if (
      this.props.signals?.[SUCCESS_REQUEST] &&
      !prevProps.signals?.[SUCCESS_REQUEST] &&
      this.state.collapseForm
    ) {
      this.setState({ collapseForm: false });
    }

    if (this.props.sectionPk !== prevProps.sectionPk) {
      this.updateCommentsList();
    }
  }

  toggleFormDisplay = () => {
    this.setState(state => {
      return { collapseForm: !state.collapseForm };
    });
  };

  handleFilterChange = (filter: string) => {
    this.setState({ selectedFilter: filter });
  };

  toggleExpandAll = () => {
    this.setState(state => ({ expandAll: !state.expandAll }));
  };

  render() {
    try {
      const comments = !isEmpty(this.props.data) ? this.props.data : [];
      const sectionAPIKwargs = this.getSectionAPIKwargs();
      const { add_issue, ...rest } = this.props.permissions || {};
      const { anchors = [] } = this.props;

      return (
        <div
          className="acr-comments-panel"
          data-test-id="comments-panel"
          id="acr-comments-wrapper"
        >
          <div className="acr-comments-panel-header">
            <DisplayToggleWrapper
              targetId={COMMENTS_TARGET_ID}
              component={Button}
              className="comments-close"
              title="Close comments"
            >
              <Close width="2em" height="1.5em" color="#fff" />
            </DisplayToggleWrapper>
            <h3
              className="comments-panel-title"
              data-test-id="comments-panel-title"
            >
              <div className="comments-title-wrapper">
                <FontAwesomeIcon
                  icon={this.state.expandAll ? faChevronDown : faChevronRight}
                  onClick={this.toggleExpandAll}
                  className="expand-all-chevron"
                />
                <div>
                  <Text kind="h3">
                    {comments.length}{' '}
                    {comments.length === 1 ? 'Comment' : 'Comments'}
                  </Text>
                  <div className="comments-important-count">
                    <span>{comments.filter(c => c.important).length}</span>
                    <FontAwesomeIcon icon={faStar} className="star-icon" />
                    <span>Important</span>
                  </div>
                </div>
              </div>
              {add_issue && !this.props.reportIsPublished ? (
                <Button
                  className="add-comment-button"
                  data-test-id="new-comment-button"
                  onClick={this.toggleFormDisplay}
                  disabled={
                    !!this.props.draftComment || this.state.collapseForm
                  }
                >
                  <AddCircle color="#fff" />
                  <span className="add-comment-button-title">New Comment</span>
                </Button>
              ) : (
                ''
              )}
            </h3>
            <div className="comments-links-wrapper">
              <div className="issues-overview-link-wrapper">
                <NavLink
                  to={
                    generatePath(
                      this.props.routesConfig.root.sub_routes.all_comments.path,
                      {
                        year: String(this.props.period),
                        cpb: this.props.project_code,
                      },
                    ) + '/'
                  }
                  target="_blank"
                  className="issues-overview-link"
                  data-test-id="narrative-and-comments-link"
                >
                  {this.props.crf?.viewCommentsLabel}
                </NavLink>
              </div>

              <IssuesDownloadDocButton
                period={this.props.period as string}
                projectCode={this.props.project_code}
                gaPosition="sidebar"
                sectionId={this.props.sectionPk as number}
              />
            </div>

            <CommentListFilterSet
              selectedFilter={this.state.selectedFilter}
              onFilterChange={this.handleFilterChange}
            />
          </div>

          {this.state.collapseForm && (
            <CommentForm
              toggleFormDisplay={this.toggleFormDisplay}
              sectionAPIKwargs={sectionAPIKwargs}
              anchors={anchors}
              commentsContainerID={this.props.id}
              cleanForm={this.props.signals[SUCCESS_REQUEST]}
            />
          )}

          {this.props?.draftComment && (
            <CommentForm
              sectionAPIKwargs={sectionAPIKwargs}
              anchors={anchors}
              commentsContainerID={this.props.id}
              cleanForm={this.props.signals[SUCCESS_REQUEST]}
              draftComment={this.props?.draftComment}
            />
          )}

          <CommentList
            sectionAPIKwargs={sectionAPIKwargs}
            commentsContainerID={this.props.id}
            comments={comments}
            user={this.props.user}
            permissions={rest}
            selectedFilter={this.state.selectedFilter}
            expandAll={this.state.expandAll}
          />
        </div>
      );
    } catch (_e) {
      return null;
    }
  }
}

export const mapStateToProps = (
  state: RootState,
  ownProps: {
    id?: string;
    reportPk?: string | number;
    officePk?: string | number;
    sectionPk?: string | number;
  },
) => {
  let props: any = {};
  const draftComment = !state.viewState?.draftComment?.isCompleted
    ? state.viewState?.draftComment
    : undefined;

  try {
    const sectionData = state.viewData[SECTION_DETAIL_DATA_KEY]?.data;
    const reportData = state.viewData[REPORT_DETAIL_DATA_KEY]?.data;

    props = {
      anchors: sectionData?.anchors,
      reportPk: sectionData?.report?.id || ownProps.reportPk,
      officePk: sectionData?.report?.office_code || ownProps.officePk,
      sectionPk: sectionData?.id || ownProps.sectionPk,
      reportIsPublished: reportData?.state === 'Published',
      permissions: reportData?.permissions?.comment_permissions,
      user: reportData?.username,
      period: reportData?.period,
      project_code: reportData?.project_code,
      crf: resolveCrf(state.viewData.ACR_REPORTS_DETAIL?.data?.crf),
      draftComment,
      routesConfig: state.routesConfig,
    };
  } catch (_e) {
    props = {};
  }

  return {
    ...props,
    data: state.viewData[COMMENTS_LIST]?.data,
    hasData: !!state.viewData[COMMENTS_LIST]?.data,
    isFetching: !!state.viewData[COMMENTS_LIST]?.isFetching,
    signals: state.componentSignals?.[ownProps.id] || {},
  };
};

export const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchCommentsRelatedToSection: (params: any = {}) => {
      dispatch(requestAPI(COMMENTS_LIST, params));
    },
    cleanSignals: (signalsToDrop: any) => {
      dispatch(cleanSignalsAction(signalsToDrop));
    },
  };
};

const CommentPanel = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CommentPanelComponent) as React.ComponentType<any>;

export default CommentPanel;
